/**
 * Exercise Library Context - Central intelligence for exercise data
 * 
 * This context provides:
 * - Access to the full exercise database
 * - Fuzzy matching to find exercises by name
 * - Filtering by category, sport, difficulty, equipment
 * - Reusable modal for showing exercise details
 * 
 * Used across: TodaysPlanDashboard, WorkoutsPage, GameDayRoutinePage
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Play, Clock, Dumbbell, Zap, Wind, Target, Sparkles, 
  Youtube, ExternalLink, X, ChevronRight
} from 'lucide-react';

// ============== EXERCISE DATABASE ==============
// Comprehensive database of exercises with video tutorials

const exerciseDatabase = [
  // ============== SPEED EXERCISES ==============
  // Foundation Speed
  {
    id: 'sprint_10yd',
    name: '10-Yard Sprint',
    aliases: ['10 yard sprint', '10yd sprint', 'short sprint', 'acceleration drill'],
    category: 'speed',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5-10 min',
    sets: '6-8',
    reps: '1 sprint',
    rest: '30-45 sec',
    description: 'Explosive short burst acceleration. Focus on driving knees and pumping arms.',
    instructions: [
      'Start in athletic stance or 3-point stance',
      'Explode forward driving knees high',
      'Pump arms aggressively',
      'Stay low for first 3 steps',
      'Gradually rise to upright sprint position'
    ],
    coachTips: 'First 3 steps are everything. Stay patient and don\'t pop up too early.',
    videoUrl: 'https://www.youtube.com/watch?v=xXn0fwbJqH8',
    videoTitle: '10 Yard Sprint Technique'
  },
  {
    id: 'sprint_20_30yd',
    name: 'Sprint Mechanics (20-30yd)',
    aliases: ['sprint mechanics', 'acceleration work', '20 yard sprint', '30 yard sprint', 'form sprints'],
    category: 'speed',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '10-15 min',
    sets: '6',
    reps: '20-30 yard accelerations',
    rest: 'Walk back (60-90 sec)',
    description: 'Focus on proper sprint mechanics and acceleration form. Build speed through technique.',
    instructions: [
      'Start in athletic stance with slight forward lean',
      'Drive knees up explosively - "punch the ground"',
      'Arms pump powerfully at 90 degrees',
      'Stay low for first 5 steps, then gradually rise',
      'Land on balls of feet, not heels',
      'Focus on form over pure speed'
    ],
    coachTips: 'Speed is earned in silence. Quality reps build fast athletes - never sacrifice form for time.',
    videoUrl: 'https://www.youtube.com/watch?v=xXn0fwbJqH8',
    videoTitle: 'Sprint Mechanics Breakdown'
  },
  {
    id: 'sprint_40yd',
    name: '40-Yard Dash',
    aliases: ['40 yard dash', '40yd dash', 'combine sprint', 'nfl combine'],
    category: 'speed',
    sports: ['football', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '10-15 min',
    sets: '4-6',
    reps: '1 sprint',
    rest: '2-3 min',
    description: 'The gold standard football speed test. Full acceleration to top-end speed.',
    instructions: [
      'Start in 3-point or 4-point stance',
      'Drive out low for first 10 yards',
      'Transition to upright sprinting',
      'Maintain form through finish line',
      'Run through the line, don\'t slow down early'
    ],
    coachTips: 'The 40 is won in the first 10 yards. Master your start.',
    videoUrl: 'https://www.youtube.com/watch?v=Dcu9R8N1Pwg',
    videoTitle: '40 Yard Dash Training'
  },
  {
    id: 'sprints_6x40',
    name: 'Sprints 6x40yd',
    aliases: ['40 yard sprints', 'sprint workout', 'speed sprints', '6x40'],
    category: 'speed',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '15-20 min',
    sets: '6',
    reps: '40 yard sprint',
    rest: 'Walk back (90 sec)',
    description: 'Classic speed workout - 6 full-effort 40-yard sprints with full recovery.',
    instructions: [
      'Mark out 40 yards with cones or landmarks',
      'Start in athletic stance or 3-point stance',
      'Explode out and accelerate through entire 40 yards',
      'Run THROUGH the finish line at full speed',
      'Walk back to start for recovery (about 90 seconds)',
      'Repeat for all 6 sprints with consistent effort'
    ],
    coachTips: 'Work while they rest. Every sprint should feel like game speed.',
    videoUrl: 'https://www.youtube.com/watch?v=Dcu9R8N1Pwg',
    videoTitle: '40 Yard Sprint Training'
  },
  {
    id: 'hill_sprints',
    name: 'Hill Sprints',
    aliases: ['hill sprint', 'incline sprint', 'hill run', 'uphill sprint', 'hill sprints 8x'],
    category: 'speed',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '15-20 min',
    sets: '8',
    reps: '1 sprint (20-40 yards)',
    rest: 'Walk back down',
    description: 'Build explosive power and acceleration on an incline. The resistance creates champions.',
    instructions: [
      'Find a moderate hill (10-15% grade)',
      'Start at bottom in athletic stance',
      'Sprint up with high knees and powerful arm drive',
      'Lean INTO the hill, don\'t stand straight up',
      'Walk back down for full recovery',
      'Focus on powerful push-off each step'
    ],
    coachTips: 'Hills build champions. The incline forces proper acceleration mechanics naturally.',
    videoUrl: 'https://www.youtube.com/watch?v=y-bLQRW4X_I',
    videoTitle: 'Hill Sprint Workout'
  },
  {
    id: 'speed_chute',
    name: 'Speed Chute Sprints',
    aliases: ['parachute sprint', 'resistance sprint', 'chute run', 'speed parachute'],
    category: 'speed',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '15 min',
    sets: '5',
    reps: '30-40 yard sprint',
    rest: '90-120 sec',
    description: 'Resistance sprinting with a parachute to build explosive acceleration power.',
    instructions: [
      'Attach speed chute harness securely around waist',
      'Start in athletic stance with chute behind you',
      'Explode forward - chute will provide resistance',
      'Drive knees high and pump arms hard to overcome drag',
      'Sprint through full 30-40 yards',
      'Walk back slowly, reset chute for next rep'
    ],
    coachTips: 'Feel the burn in your hips and glutes - that\'s where speed is built.',
    videoUrl: 'https://www.youtube.com/watch?v=BYPlA0wRtPE',
    videoTitle: 'Speed Chute Training'
  },
  {
    id: 'resisted_runs',
    name: 'Resisted Runs',
    aliases: ['banded sprints', 'resistance runs', 'partner resisted sprint', 'sled push'],
    category: 'speed',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '15 min',
    sets: '6',
    reps: '15-20 yards',
    rest: '60-90 sec',
    description: 'Sprinting against resistance (bands, partner, or sled) to build explosive starting power.',
    instructions: [
      'Secure resistance band around waist OR have partner hold band behind you',
      'Start in athletic stance leaning forward',
      'Drive hard against resistance for 15-20 yards',
      'Focus on powerful leg drive and arm action',
      'Maintain forward lean throughout',
      'Release resistance and finish sprint freely (contrast effect)'
    ],
    coachTips: 'Resistance training transfers directly to faster first-step acceleration.',
    videoUrl: 'https://www.youtube.com/watch?v=Jx1TNFO4kXk',
    videoTitle: 'Resisted Sprint Training'
  },
  {
    id: 'shuffle_to_sprint',
    name: 'Shuffle to Sprint',
    aliases: ['lateral to sprint', 'shuffle sprint transition', 'defensive burst'],
    category: 'speed',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '10-15 min',
    sets: '4-6',
    reps: '3 each direction',
    rest: '45-60 sec',
    description: 'Transition from lateral shuffle to explosive forward sprint - essential for game speed.',
    instructions: [
      'Start in athletic defensive stance',
      'Shuffle laterally 5 yards maintaining low position',
      'On cue, plant outside foot hard and explode into forward sprint',
      'Accelerate for 10-15 yards at full speed',
      'Focus on quick hip turn and explosive first step',
      'Repeat shuffling opposite direction'
    ],
    coachTips: 'This drill mimics real game situations. Quick hips = quick feet.',
    videoUrl: 'https://www.youtube.com/watch?v=aHxSMBtTqdM',
    videoTitle: 'Shuffle to Sprint Drill'
  },
  {
    id: 'speed_endurance_shuttles',
    name: 'Speed Endurance Shuttles',
    aliases: ['conditioning shuttles', '100 yard conditioning', 'speed conditioning', '60yd runs'],
    category: 'speed',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'advanced',
    equipment: 'none',
    duration: '20-25 min',
    sets: '6-8',
    reps: '1 shuttle (100 yards total)',
    rest: '60-90 sec',
    description: 'Build speed endurance to maintain top speed throughout entire games.',
    instructions: [
      'Set up cones at 0, 25, 50, 75, and 100 yards',
      'Sprint to 25, touch line, sprint back to start',
      'Immediately sprint to 50, touch, back to start',
      'Continue pattern through 75 and 100',
      'Focus on maintaining speed even when tired',
      'Rest 60-90 seconds between shuttles'
    ],
    coachTips: 'Fourth quarter speed wins games. Push through when it burns.',
    videoUrl: 'https://www.youtube.com/watch?v=Dcu9R8N1Pwg',
    videoTitle: 'Speed Endurance Conditioning'
  },
  // No-Equipment Speed Alternatives
  {
    id: 'chalk_line_hops',
    name: 'Chalk Line Hops',
    aliases: ['line hops', 'no equipment ladder', 'sidewalk drills', 'chalk drills'],
    category: 'speed',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '8-10 min',
    sets: '3-4',
    reps: '30 seconds each pattern',
    rest: '20 sec',
    description: 'No-equipment alternative to ladder drills using chalk lines or sidewalk cracks.',
    instructions: [
      'Draw lines with chalk OR use existing sidewalk cracks',
      'Hop forward over each line as fast as possible',
      'Land on balls of feet, stay light',
      'Variations: single leg, lateral, in-and-out',
      'Focus on quick ground contact time',
      'Keep arms pumping to maintain rhythm'
    ],
    coachTips: 'No equipment? No excuses. Champions find a way.',
    videoUrl: 'https://www.youtube.com/watch?v=aHxSMBtTqdM',
    videoTitle: 'No Equipment Speed Drills'
  },

  // ============== AGILITY EXERCISES ==============
  // Agility Ladder Drills (10 min session from your program)
  {
    id: 'ladder_basic',
    name: 'Agility Ladder - Quick Feet',
    aliases: ['ladder drills', 'quick feet', 'footwork drill', 'agility ladder', 'quick feet drill'],
    category: 'agility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'basic',
    duration: '10 min',
    sets: '3-4',
    reps: '2 lengths each pattern',
    rest: '30 sec',
    description: 'Foundation footwork drill for quick, precise foot placement. Part of Tuesday Speed & Agility.',
    instructions: [
      'Start at one end of the ladder',
      'Step BOTH feet into each box as quickly as possible',
      'Stay on balls of feet - never flat-footed',
      'Keep hips low and arms pumping',
      'Focus on speed AND precision',
      'Land softly - quick ground contact'
    ],
    coachTips: 'Slow is smooth, smooth is fast. Master the pattern before adding speed.',
    videoUrl: 'https://www.youtube.com/watch?v=aHxSMBtTqdM',
    videoTitle: 'Agility Ladder Drills for Beginners'
  },
  {
    id: 'ladder_in_out',
    name: 'Agility Ladder - In-and-Outs',
    aliases: ['in and out', 'in out drill', 'lateral in out', 'ladder lateral'],
    category: 'agility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'basic',
    duration: '10 min',
    sets: '3-4',
    reps: '2 lengths',
    rest: '30 sec',
    description: 'Step in and out of ladder boxes laterally - develops lateral quickness.',
    instructions: [
      'Stand beside ladder, facing sideways',
      'Step both feet INTO the first box',
      'Step both feet OUT the other side',
      'Move down the ladder staying light',
      'Pump arms to maintain rhythm',
      'Keep hips low throughout'
    ],
    coachTips: 'Think "in-in-out-out" rhythm. Quick feet, soft landings.',
    videoUrl: 'https://www.youtube.com/watch?v=aHxSMBtTqdM',
    videoTitle: 'In-and-Out Ladder Drill'
  },
  {
    id: 'ladder_ickey',
    name: 'Agility Ladder - Ickey Shuffle',
    aliases: ['ickey shuffle', 'icky shuffle', 'ickey drill', 'nfl shuffle'],
    category: 'agility',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10 min',
    sets: '3-4',
    reps: '2 lengths',
    rest: '30 sec',
    description: 'Classic NFL footwork pattern - named after Ickey Woods. Develops coordination and quick feet.',
    instructions: [
      'Start at the side of the ladder',
      'Step IN with lead foot, then trail foot (2 feet in)',
      'Step OUT with lead foot to side of ladder',
      'Step IN with trail foot, then new lead foot',
      'Step OUT with trail foot to other side',
      'Continue the zigzag pattern down ladder'
    ],
    coachTips: 'It\'s a 3-step pattern: in-in-out. Master slowly, then add speed.',
    videoUrl: 'https://www.youtube.com/watch?v=aHxSMBtTqdM',
    videoTitle: 'Ickey Shuffle Tutorial'
  },
  {
    id: 'ladder_lateral_shuffle',
    name: 'Agility Ladder - Lateral Shuffles',
    aliases: ['lateral shuffle', 'side shuffle', 'lateral ladder', 'side to side'],
    category: 'agility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'basic',
    duration: '10 min',
    sets: '3-4',
    reps: '2 lengths each direction',
    rest: '30 sec',
    description: 'Move sideways through ladder - essential for defensive positioning.',
    instructions: [
      'Stand at end of ladder, facing down its length',
      'Move sideways, placing both feet in each box',
      'Lead foot enters, trail foot follows',
      'Stay low in athletic stance',
      'Don\'t let feet touch or cross',
      'Pump arms for balance and speed'
    ],
    coachTips: 'Defensive athletes live here. Stay low, stay quick.',
    videoUrl: 'https://www.youtube.com/watch?v=aHxSMBtTqdM',
    videoTitle: 'Lateral Shuffle Drill'
  },
  {
    id: 'ladder_1in_2in',
    name: 'Ladder 1-In/2-In Drill',
    aliases: ['one in two in', '1-2 drill', 'ladder combo'],
    category: 'agility',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10 min',
    sets: '3-4',
    reps: '2 lengths',
    rest: '30 sec',
    description: 'Alternating 1-foot and 2-feet patterns through ladder for advanced coordination.',
    instructions: [
      'Start at ladder end in athletic stance',
      'First box: one foot in, step forward',
      'Second box: both feet in quickly',
      'Continue alternating 1-in, 2-in pattern',
      'Stay light on balls of feet',
      'Maintain arm pump throughout'
    ],
    coachTips: 'This drill builds the brain-body connection. Concentrate on the pattern.',
    videoUrl: 'https://www.youtube.com/watch?v=aHxSMBtTqdM',
    videoTitle: 'Advanced Ladder Patterns'
  },
  // Cone Drills
  {
    id: 'pro_agility',
    name: 'Pro Agility (5-10-5) Shuttle',
    aliases: ['5-10-5', 'pro agility shuttle', 'short shuttle', '20 yard shuttle', 'shuttle runs'],
    category: 'agility',
    sports: ['football', 'basketball', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10-15 min',
    sets: '4',
    reps: '1',
    rest: '1-2 min',
    description: 'The NFL Combine standard for change of direction. Sprint 5 yards, 10 yards, 5 yards back.',
    instructions: [
      'Start in 3-point stance at middle cone/line',
      'Sprint 5 yards to one side, touch line with hand',
      'Explode! Sprint 10 yards to opposite side, touch line',
      'Sprint 5 yards back through start position',
      'Stay LOW on cuts - power comes from legs',
      'Plant hard on outside foot for direction changes'
    ],
    coachTips: 'Win the test on the turns. Plant and drive, don\'t round corners.',
    videoUrl: 'https://www.youtube.com/watch?v=DJkKr4Mdfew',
    videoTitle: 'Pro Agility Drill Technique'
  },
  {
    id: 'cone_w_drill',
    name: 'Cone "W" Drill (WR/DB Breaks)',
    aliases: ['w drill', 'wr drill', 'db drill', 'receiver cuts', 'corner drill'],
    category: 'agility',
    sports: ['football'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10-15 min',
    sets: '6',
    reps: '1 each side',
    rest: '45-60 sec',
    description: 'Football-specific drill for wide receivers and defensive backs. Practice sharp cuts and breaks.',
    instructions: [
      'Set up 5 cones in a "W" pattern (10 yards deep, 5 yards between points)',
      'Start at first cone in athletic stance',
      'Sprint to second cone, plant and cut at 45 degrees',
      'Sprint to third cone (apex), plant hard',
      'Cut back at 45 degrees to fourth cone',
      'Finish through fifth cone at full speed'
    ],
    coachTips: 'This is where routes are won. Sell the fake, explode out of the break.',
    videoUrl: 'https://www.youtube.com/watch?v=_yJZHlXAOhM',
    videoTitle: 'W Drill for Receivers'
  },
  {
    id: 'cone_drills',
    name: 'Cone Drills - T-Drill',
    aliases: ['t-drill', 't drill', 'cone drill', 't-test'],
    category: 'agility',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10 min',
    sets: '4-6',
    reps: '1',
    rest: '1 min',
    description: 'Multi-directional agility combining forward, lateral, and backpedal movements.',
    instructions: [
      'Set up 4 cones in a T shape (10 yards up, 5 yards each side)',
      'Sprint forward to middle cone',
      'Shuffle left to cone, touch',
      'Shuffle right to far cone, touch',
      'Shuffle back to middle, backpedal to start'
    ],
    coachTips: 'Keep your hips square during shuffles. Don\'t cross your feet.',
    videoUrl: 'https://www.youtube.com/watch?v=_yJZHlXAOhM',
    videoTitle: 'T-Drill for Athletes'
  },
  {
    id: 'cone_zigzag',
    name: 'Cone Zig-Zag',
    aliases: ['zigzag drill', 'zig zag', 'weave drill', 'slalom cones'],
    category: 'agility',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10 min',
    sets: '4',
    reps: '1 full pattern',
    rest: '45-60 sec',
    description: 'Weave through cones at full speed - develops cutting ability and hip mobility.',
    instructions: [
      'Set 6 cones in a zig-zag pattern (3-5 yards apart)',
      'Sprint to first cone and cut sharply around it',
      'Explode to second cone, cut to opposite side',
      'Continue weaving through all cones at full speed',
      'Focus on low hips and quick direction changes',
      'Plant hard on outside foot at each cone'
    ],
    coachTips: 'Stay low! High hips = slow cuts. Get your butt down.',
    videoUrl: 'https://www.youtube.com/watch?v=_yJZHlXAOhM',
    videoTitle: 'Zig-Zag Cone Drill'
  },
  {
    id: 'cone_figure8',
    name: 'Figure 8 Runs',
    aliases: ['figure 8', 'figure eight', '8 drill', 'continuous cut'],
    category: 'agility',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'basic',
    duration: '8-10 min',
    sets: '4-6',
    reps: '3 full figure 8s',
    rest: '30-45 sec',
    description: 'Sprint in a figure-eight pattern around two cones. Builds continuous cutting endurance.',
    instructions: [
      'Place two cones 10 yards apart',
      'Start at one cone in athletic stance',
      'Sprint around first cone making tight curve',
      'Sprint to second cone and circle it opposite direction',
      'Continue the figure-8 pattern for 3 full loops',
      'Stay low and lean into your turns'
    ],
    coachTips: 'Lean into the turn like a motorcycle. Let your body angle create the cut.',
    videoUrl: 'https://www.youtube.com/watch?v=_yJZHlXAOhM',
    videoTitle: 'Figure 8 Agility Drill'
  },
  {
    id: 'lateral_bounds',
    name: 'Lateral Bounds',
    aliases: ['side bounds', 'lateral jumps', 'skater bounds', 'side to side jumps'],
    category: 'agility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '10 min',
    sets: '3',
    reps: '10 each direction',
    rest: '45 sec',
    description: 'Explosive side-to-side jumps developing lateral power and single-leg stability.',
    instructions: [
      'Start on one leg, other leg bent behind',
      'Explode laterally off standing leg',
      'Land softly on opposite leg',
      'Absorb landing, then immediately bound back',
      'Swing arms for momentum and balance',
      'Focus on sticking each landing before next bound'
    ],
    coachTips: 'Control the landing first, then add speed. Ankles and knees should be stable.',
    videoUrl: 'https://www.youtube.com/watch?v=DJkKr4Mdfew',
    videoTitle: 'Lateral Bounds Tutorial'
  },
  // No-Equipment Agility Alternatives
  {
    id: 'shoe_marker_drills',
    name: 'Shoe/Bottle Marker Drills',
    aliases: ['no equipment cones', 'home agility', 'marker drills', 'bottle drills'],
    category: 'agility',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '10-15 min',
    sets: '4',
    reps: 'Full pattern each',
    rest: '30-45 sec',
    description: 'Use shoes, water bottles, or any objects as cone replacements for agility work anywhere.',
    instructions: [
      'Place shoes/bottles in desired pattern (T, W, zig-zag)',
      'Treat markers exactly like cones',
      'Sprint, cut, shuffle around markers',
      'Maintain same intensity as gym work',
      'Get creative - stairs, curbs work too',
      'No excuses, train anywhere'
    ],
    coachTips: 'No equipment? No excuses. Champions find a way.',
    videoUrl: 'https://www.youtube.com/watch?v=aHxSMBtTqdM',
    videoTitle: 'Home Agility Training'
  },
  {
    id: 'towel_hurdles',
    name: 'Rolled Towel/Book Hurdles',
    aliases: ['home hurdles', 'mini hurdles', 'towel drills', 'obstacle drills'],
    category: 'agility',
    sports: ['football', 'basketball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '10 min',
    sets: '3-4',
    reps: '2 lengths',
    rest: '30 sec',
    description: 'No mini hurdles? Use rolled towels or stacked books for quick feet drills.',
    instructions: [
      'Roll up towels or stack books (4-6 inches high)',
      'Place 6-8 obstacles in a line, 18 inches apart',
      'Quickly step over each obstacle with high knees',
      'Land softly on balls of feet',
      'Progress to single-leg hops over obstacles',
      'Focus on quick ground contact'
    ],
    coachTips: 'Make do with what you have. Same drill, different tools.',
    videoUrl: 'https://www.youtube.com/watch?v=aHxSMBtTqdM',
    videoTitle: 'Home Quick Feet Drills'
  },

  // STRENGTH EXERCISES
  {
    id: 'pushups',
    name: 'Push-Ups',
    aliases: ['pushup', 'push up', 'press up', 'chest press bodyweight'],
    category: 'strength',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5 min',
    sets: '3-4',
    reps: '10-20',
    rest: '45 sec',
    description: 'Foundation upper body exercise building chest, shoulders, and triceps.',
    instructions: [
      'Start in plank position, hands shoulder-width',
      'Lower chest to floor, elbows at 45 degrees',
      'Keep core tight, body in straight line',
      'Push back up to start position',
      'Full range of motion each rep'
    ],
    coachTips: 'Quality over quantity. A proper push-up beats 10 sloppy ones.',
    videoUrl: 'https://www.youtube.com/watch?v=IODxDxX7oi4',
    videoTitle: 'Perfect Push-Up Form'
  },
  {
    id: 'squats',
    name: 'Bodyweight Squats',
    aliases: ['squat', 'air squat', 'bodyweight squat', 'bw squat'],
    category: 'strength',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5-10 min',
    sets: '3-4',
    reps: '15-20',
    rest: '45 sec',
    description: 'Foundation lower body movement for leg strength and mobility.',
    instructions: [
      'Stand feet shoulder-width apart',
      'Push hips back and down like sitting in a chair',
      'Keep chest up and knees tracking over toes',
      'Go as low as mobility allows (ideally thighs parallel)',
      'Drive through heels to stand'
    ],
    coachTips: 'Depth builds athletes. Work on mobility to squat deeper over time.',
    videoUrl: 'https://www.youtube.com/watch?v=aclHkVaku9U',
    videoTitle: 'Perfect Squat Form'
  },
  {
    id: 'lunges',
    name: 'Walking Lunges',
    aliases: ['lunge', 'forward lunge', 'walking lunge', 'split squat'],
    category: 'strength',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5-10 min',
    sets: '3',
    reps: '10-12 each leg',
    rest: '45 sec',
    description: 'Single-leg strength and balance exercise for athletic development.',
    instructions: [
      'Step forward into a lunge position',
      'Lower back knee toward ground',
      'Keep front knee over ankle, not past toes',
      'Push through front heel to step forward',
      'Alternate legs with each step'
    ],
    coachTips: 'Control the descent. The eccentric phase builds strength.',
    videoUrl: 'https://www.youtube.com/watch?v=L8fvypPrzzs',
    videoTitle: 'Walking Lunges Tutorial'
  },
  {
    id: 'plank',
    name: 'Plank Hold',
    aliases: ['plank', 'front plank', 'core hold', 'isometric plank'],
    category: 'strength',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5 min',
    sets: '3-4',
    reps: '30-60 sec hold',
    rest: '30 sec',
    description: 'Core stability exercise building the foundation for all athletic movement.',
    instructions: [
      'Start on forearms and toes',
      'Keep body in straight line from head to heels',
      'Engage core, squeeze glutes',
      'Don\'t let hips sag or pike up',
      'Breathe steadily throughout hold'
    ],
    coachTips: 'A strong core is the foundation. Don\'t sacrifice form for time.',
    videoUrl: 'https://www.youtube.com/watch?v=pSHjTRCQxIw',
    videoTitle: 'Perfect Plank Form'
  },

  // PLYOMETRICS
  {
    id: 'box_jumps',
    name: 'Box Jumps',
    aliases: ['box jump', 'plyo box', 'jump box', 'plyometric jump'],
    category: 'plyometrics',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'intermediate',
    equipment: 'basic',
    duration: '10 min',
    sets: '4-5',
    reps: '5-8',
    rest: '1 min',
    description: 'Explosive lower body power development for vertical jump and acceleration.',
    instructions: [
      'Stand facing box, feet hip-width apart',
      'Swing arms back and load legs',
      'Explode up, driving arms forward',
      'Land softly with knees bent on box',
      'Step down (don\'t jump down) to reset'
    ],
    coachTips: 'Land soft like a cat. Hard landings mean the box is too high.',
    videoUrl: 'https://www.youtube.com/watch?v=52r_Ul5k03g',
    videoTitle: 'Box Jump Technique'
  },
  {
    id: 'broad_jump',
    name: 'Broad Jump (Standing Long Jump)',
    aliases: ['broad jump', 'standing long jump', 'horizontal jump'],
    category: 'plyometrics',
    sports: ['football', 'basketball', 'general'],
    difficulty: 'intermediate',
    equipment: 'none',
    duration: '10 min',
    sets: '5-6',
    reps: '3-5',
    rest: '1-2 min',
    description: 'Horizontal explosive power - key combine metric for football.',
    instructions: [
      'Stand with feet shoulder-width apart',
      'Swing arms back, bend knees to load',
      'Explode forward and up at 45 degrees',
      'Drive arms forward aggressively',
      'Land in athletic position, absorb impact'
    ],
    coachTips: 'Arm swing adds 6+ inches. Don\'t neglect upper body involvement.',
    videoUrl: 'https://www.youtube.com/watch?v=96z7aAjy0gE',
    videoTitle: 'Broad Jump Training'
  },
  {
    id: 'depth_jumps',
    name: 'Depth Jumps',
    aliases: ['depth jump', 'drop jump', 'reactive jump'],
    category: 'plyometrics',
    sports: ['football', 'basketball', 'general'],
    difficulty: 'advanced',
    equipment: 'basic',
    duration: '10 min',
    sets: '3-4',
    reps: '4-6',
    rest: '2 min',
    description: 'Advanced plyometric for reactive strength and vertical jump improvement.',
    instructions: [
      'Stand on box (12-24 inches)',
      'Step off (don\'t jump off) the box',
      'Land and immediately explode upward',
      'Minimize ground contact time',
      'Land softly in athletic position'
    ],
    coachTips: 'This is advanced. Master box jumps first. Quality over quantity.',
    videoUrl: 'https://www.youtube.com/watch?v=Jx1TNFO4kXk',
    videoTitle: 'Depth Jump Tutorial'
  },

  // FLEXIBILITY/RECOVERY
  {
    id: 'dynamic_stretch',
    name: 'Dynamic Stretching Routine',
    aliases: ['dynamic stretch', 'warm up', 'active stretch', 'movement prep'],
    category: 'flexibility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '10 min',
    sets: '1',
    reps: '10 each movement',
    rest: 'None',
    description: 'Pre-workout movement preparation to activate muscles and increase range of motion.',
    instructions: [
      'Leg swings (front/back, side to side)',
      'Walking knee hugs',
      'Walking quad pulls',
      'Lunge with rotation',
      'High knees and butt kicks'
    ],
    coachTips: 'Dynamic before training, static after. Never skip the warm-up.',
    videoUrl: 'https://www.youtube.com/watch?v=nPHfEnZD1Wk',
    videoTitle: 'Dynamic Warm-Up Routine'
  },
  {
    id: 'foam_rolling',
    name: 'Foam Rolling Recovery',
    aliases: ['foam roll', 'myofascial release', 'smr', 'roller'],
    category: 'flexibility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'basic',
    duration: '10-15 min',
    sets: '1',
    reps: '60 sec each area',
    rest: 'None',
    description: 'Self-myofascial release for muscle recovery and mobility.',
    instructions: [
      'Position body part on foam roller',
      'Roll slowly, 1 inch per second',
      'Pause on tender spots for 30-60 sec',
      'Focus on major muscle groups (quads, IT band, glutes, upper back)',
      'Avoid rolling directly on joints'
    ],
    coachTips: 'Pain is feedback. Breathe through tender spots; don\'t tense up.',
    videoUrl: 'https://www.youtube.com/watch?v=SxQkWUJ1Tso',
    videoTitle: 'Full Body Foam Rolling'
  },
  {
    id: 'hip_mobility',
    name: 'Hip Mobility Routine',
    aliases: ['hip stretch', 'hip opener', '90/90 stretch', 'hip mobility'],
    category: 'flexibility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '10 min',
    sets: '1',
    reps: '30-60 sec each position',
    rest: 'None',
    description: 'Hip flexibility routine critical for athletic performance and injury prevention.',
    instructions: [
      '90/90 hip stretch both sides',
      'Pigeon pose each side',
      'Frog stretch',
      'Couch stretch for hip flexors',
      'Deep squat hold'
    ],
    coachTips: 'Hips don\'t lie. Limited hip mobility limits EVERYTHING athletic.',
    videoUrl: 'https://www.youtube.com/watch?v=SsT_go-oCcQ',
    videoTitle: 'Hip Mobility for Athletes'
  },
  {
    id: 'light_jog',
    name: 'Light Jog',
    aliases: ['easy jog', 'warm up jog', 'recovery run', 'light run'],
    category: 'flexibility',
    sports: ['football', 'basketball', 'baseball', 'soccer', 'general'],
    difficulty: 'beginner',
    equipment: 'none',
    duration: '5-10 min',
    sets: '1',
    reps: 'Continuous',
    rest: 'None',
    description: 'Low-intensity movement to warm up muscles and prepare for activity.',
    instructions: [
      'Start at walking pace',
      'Gradually increase to light jog',
      'Maintain conversational pace',
      'Focus on relaxed breathing',
      'Keep arms loose, shoulders relaxed'
    ],
    coachTips: 'This is NOT a workout. Keep it easy to prime the body.',
    videoUrl: 'https://www.youtube.com/watch?v=brFHyOtTwH4',
    videoTitle: 'Proper Jogging Form'
  }
];

// ============== SPEED TRAINING PROGRAMS ==============
// Pre-built programs for athletes with speed goals

const speedPrograms = {
  // Tuesday Speed & Agility Session (45 min) from the training program
  tuesdaySpeedAgility: {
    id: 'tuesday_speed_agility',
    name: 'Tuesday Speed & Agility Session',
    description: 'Complete 45-minute speed and agility workout from the Aura training program',
    duration: '45 min',
    difficulty: 'intermediate',
    structure: [
      { phase: 'Warm-up', duration: '5 min', exercises: ['dynamic_stretch', 'light_jog'] },
      { phase: 'Sprint Mechanics', duration: '10 min', exercises: ['sprint_20_30yd'], sets: '6 x 20-30yd' },
      { phase: 'Agility Ladder', duration: '10 min', exercises: ['ladder_basic', 'ladder_lateral_shuffle', 'ladder_ickey'], notes: 'Quick feet, lateral shuffles, Ickey shuffle' },
      { phase: 'Cone Work', duration: '12 min', exercises: ['cone_w_drill'], sets: '6 reps each side', notes: 'DB breaks, WR cuts' },
      { phase: 'Cool Down', duration: '8 min', exercises: ['hip_mobility', 'foam_rolling'] }
    ],
    coachNotes: 'This is the standard Tuesday workout. Focus on QUALITY over speed initially.'
  },
  
  // Daily Speed Foundation (20-25 min) for everyday use
  dailySpeedFoundation: {
    id: 'daily_speed_foundation',
    name: 'Daily Speed Foundation',
    description: 'Quick daily speed work to build consistent improvement',
    duration: '20-25 min',
    difficulty: 'beginner',
    structure: [
      { phase: 'Movement Prep', duration: '5 min', exercises: ['dynamic_stretch'] },
      { phase: 'Acceleration', duration: '8 min', exercises: ['sprint_20_30yd'], sets: '4 x 20-30yd' },
      { phase: 'Quick Feet', duration: '7 min', exercises: ['ladder_basic', 'ladder_in_out'] },
      { phase: 'Stretch', duration: '5 min', exercises: ['hip_mobility'] }
    ],
    coachNotes: 'Perfect for daily training. Speed is earned in silence.'
  },

  // Week 1 Speed: Sprint Foundation
  week1Speed: {
    id: 'week1_speed',
    name: 'Week 1: Speed Foundation',
    description: 'Sprint mechanics, bodyweight strength, resisted runs',
    duration: '30-40 min',
    difficulty: 'beginner',
    exercises: ['sprint_20_30yd', 'resisted_runs', 'sprints_6x40', 'pushups', 'squats'],
    weeklyTargets: { speed: 3, strength: 3, agility: 2, recovery: 5 }
  },

  // Week 3 Speed: Explosive Power
  week3Speed: {
    id: 'week3_speed',
    name: 'Week 3: Explosive Power',
    description: 'Plyometrics, short sprints, medicine ball power work',
    duration: '35-45 min',
    difficulty: 'intermediate',
    exercises: ['hill_sprints', 'box_jumps', 'broad_jump', 'sprint_10yd'],
    weeklyTargets: { speed: 2, strength: 3, agility: 2, recovery: 5 }
  },

  // 4-Week Speed Development Program
  fourWeekSpeedProgram: {
    id: '4_week_speed_program',
    name: '4-Week Speed Development Program',
    description: 'Progressive speed training for athletes who want to get faster',
    totalWeeks: 4,
    weeks: [
      {
        week: 1,
        focus: 'Speed Foundation',
        drills: [
          { name: 'Sprints 6x40yd', category: 'speed', equipment: 'none', alt: null },
          { name: 'Push-ups 3x12', category: 'strength', equipment: 'none', alt: null },
          { name: 'Shuttle Runs 4x', category: 'agility', equipment: 'basic', alt: 'shoe_marker_drills' },
          { name: 'Stretch 10 min', category: 'recovery', equipment: 'none', alt: null }
        ],
        quote: 'Work while they rest.'
      },
      {
        week: 2,
        focus: 'Lateral Quickness',
        drills: [
          { name: 'Cone Drills 4x', category: 'speed', equipment: 'basic', alt: 'shoe_marker_drills' },
          { name: 'Bodyweight Squats 3x15', category: 'strength', equipment: 'none', alt: null },
          { name: 'Lateral Bounds 3x10', category: 'agility', equipment: 'none', alt: null },
          { name: 'Foam Roll 10 min', category: 'recovery', equipment: 'basic', alt: null }
        ],
        quote: 'Quick feet, quick mind.'
      },
      {
        week: 3,
        focus: 'Explosive Power',
        drills: [
          { name: 'Hill Sprints 8x', category: 'speed', equipment: 'none', alt: null },
          { name: 'Resistance Bands 4x10', category: 'strength', equipment: 'basic', alt: 'pushups' },
          { name: 'Cone Zig-Zag 4x', category: 'agility', equipment: 'basic', alt: 'shoe_marker_drills' },
          { name: 'Meditation 5 min', category: 'recovery', equipment: 'none', alt: null }
        ],
        quote: 'Hills build champions.'
      },
      {
        week: 4,
        focus: 'Peak Performance',
        drills: [
          { name: 'Agility Ladder', category: 'speed', equipment: 'basic', alt: 'chalk_line_hops' },
          { name: 'Weighted Vest 3x10', category: 'strength', equipment: 'basic', alt: 'squats' },
          { name: 'Speed Chute 5x', category: 'agility', equipment: 'basic', alt: 'resisted_runs' },
          { name: 'Epsom Bath', category: 'recovery', equipment: 'basic', alt: null }
        ],
        quote: 'Aura is earned, not given.'
      }
    ]
  },

  // Rain Day Indoor Speed Options
  rainDaySpeed: {
    id: 'rain_day_speed',
    name: 'Rain Day Indoor Speed',
    description: 'Indoor speed alternatives when outdoor training isn\'t possible',
    duration: '25-30 min',
    difficulty: 'beginner',
    optionA: {
      name: 'Indoor Training',
      exercises: ['pushups', 'squats', 'ladder_basic', 'plank'],
      notes: 'Jump rope, agility ladder if space allows, core circuit'
    },
    optionB: {
      name: 'Recovery Mode',
      exercises: ['dynamic_stretch', 'foam_rolling', 'hip_mobility'],
      notes: 'Morning Stretch Flow, Foam Roll, Epsom Salt Bath, Massage Gun, Hydration + Sleep'
    },
    quote: 'Recovery is training too.'
  }
};

// ============== MORNING STRETCH FLOW (Daily Habit) ==============
const morningStretchFlow = {
  id: 'morning_stretch_flow',
  name: 'Morning Stretch Flow',
  description: 'Daily 7-10 minute stretch routine - locked in as a daily habit',
  duration: '7-10 min',
  steps: [
    { step: 1, name: 'Neck Rolls', reps: '5 each direction' },
    { step: 2, name: 'Arm Circles', reps: '10 forward/back' },
    { step: 3, name: 'Torso Twists', reps: '10 each side' },
    { step: 4, name: 'Hip Opener (Butterfly Stretch)', hold: '20 sec' },
    { step: 5, name: 'Hamstring Stretch', hold: '20 sec each leg' },
    { step: 6, name: 'Quad Stretch', hold: '20 sec each leg' },
    { step: 7, name: 'Calf Stretch', hold: '20 sec each leg' },
    { step: 8, name: 'Walking Lunges', reps: '10 steps each leg' }
  ],
  quote: 'Start your day with strength. Recovery is training too.'
};

// ============== CATEGORY DEFINITIONS ==============

const skillCategories = [
  { id: 'speed', name: 'Speed', icon: Wind, color: 'bg-blue-500', textColor: 'text-blue-400' },
  { id: 'agility', name: 'Agility', icon: Zap, color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  { id: 'strength', name: 'Strength', icon: Dumbbell, color: 'bg-red-500', textColor: 'text-red-400' },
  { id: 'plyometrics', name: 'Plyometrics', icon: Target, color: 'bg-purple-500', textColor: 'text-purple-400' },
  { id: 'flexibility', name: 'Flexibility', icon: Sparkles, color: 'bg-green-500', textColor: 'text-green-400' },
];

const difficultyLevels = [
  { id: 'beginner', name: 'Beginner', color: 'text-green-400' },
  { id: 'intermediate', name: 'Intermediate', color: 'text-yellow-400' },
  { id: 'advanced', name: 'Advanced', color: 'text-red-400' },
];

// ============== CONTEXT ==============

const ExerciseLibraryContext = createContext(null);

export const useExerciseLibrary = () => {
  const context = useContext(ExerciseLibraryContext);
  if (!context) {
    throw new Error('useExerciseLibrary must be used within ExerciseLibraryProvider');
  }
  return context;
};

// ============== PROVIDER ==============

export const ExerciseLibraryProvider = ({ children }) => {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fuzzy match exercise by name
  const findExercise = useCallback((name) => {
    if (!name) return null;
    
    const normalizedName = name.toLowerCase().trim();
    
    // Exact match first
    let match = exerciseDatabase.find(ex => 
      ex.name.toLowerCase() === normalizedName
    );
    
    if (match) return match;
    
    // Check aliases
    match = exerciseDatabase.find(ex => 
      ex.aliases?.some(alias => alias.toLowerCase() === normalizedName)
    );
    
    if (match) return match;
    
    // Partial match (contains)
    match = exerciseDatabase.find(ex => 
      ex.name.toLowerCase().includes(normalizedName) ||
      normalizedName.includes(ex.name.toLowerCase()) ||
      ex.aliases?.some(alias => 
        alias.toLowerCase().includes(normalizedName) ||
        normalizedName.includes(alias.toLowerCase())
      )
    );
    
    return match || null;
  }, []);

  // Get all exercises
  const getAllExercises = useCallback(() => exerciseDatabase, []);

  // Get exercises by category
  const getExercisesByCategory = useCallback((category) => {
    return exerciseDatabase.filter(ex => ex.category === category);
  }, []);

  // Get category info
  const getCategoryInfo = useCallback((categoryId) => {
    return skillCategories.find(c => c.id === categoryId) || skillCategories[0];
  }, []);

  // Get difficulty info
  const getDifficultyInfo = useCallback((difficultyId) => {
    return difficultyLevels.find(d => d.id === difficultyId) || difficultyLevels[0];
  }, []);

  // Open exercise detail modal
  const showExerciseDetail = useCallback((exerciseOrName) => {
    let exercise = exerciseOrName;
    
    // If it's a string, find the exercise
    if (typeof exerciseOrName === 'string') {
      exercise = findExercise(exerciseOrName);
    }
    
    if (exercise) {
      setSelectedExercise(exercise);
      setIsModalOpen(true);
    }
  }, [findExercise]);

  // Close modal
  const closeExerciseDetail = useCallback(() => {
    setIsModalOpen(false);
    setSelectedExercise(null);
  }, []);

  const value = {
    // Data
    exerciseDatabase,
    skillCategories,
    difficultyLevels,
    speedPrograms,
    morningStretchFlow,
    // Methods
    findExercise,
    getAllExercises,
    getExercisesByCategory,
    getCategoryInfo,
    getDifficultyInfo,
    showExerciseDetail,
    closeExerciseDetail,
    // Speed Program helpers
    getSpeedProgram: (programId) => speedPrograms[programId] || null,
    getAllSpeedPrograms: () => Object.values(speedPrograms),
    getFourWeekSpeedProgram: () => speedPrograms.fourWeekSpeedProgram,
    // State
    selectedExercise,
    isModalOpen,
  };

  return (
    <ExerciseLibraryContext.Provider value={value}>
      {children}
      <ExerciseDetailModal />
    </ExerciseLibraryContext.Provider>
  );
};

// ============== EXERCISE DETAIL MODAL ==============

const ExerciseDetailModal = () => {
  const { selectedExercise, isModalOpen, closeExerciseDetail, getCategoryInfo, getDifficultyInfo } = useExerciseLibrary();

  if (!selectedExercise) return null;

  const catInfo = getCategoryInfo(selectedExercise.category);
  const diffInfo = getDifficultyInfo(selectedExercise.difficulty);
  const CategoryIcon = catInfo.icon;

  return (
    <Dialog open={isModalOpen} onOpenChange={closeExerciseDetail}>
      <DialogContent className="bg-aura-surface border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${catInfo.color} flex items-center justify-center`}>
                <CategoryIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-white font-heading uppercase tracking-wider text-lg">
                  {selectedExercise.name}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`${catInfo.color} text-white border-0 text-xs`}>
                    {catInfo.name}
                  </Badge>
                  <span className={`text-xs ${diffInfo.color}`}>{diffInfo.name}</span>
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Description */}
          <p className="text-aura-text-muted text-sm">{selectedExercise.description}</p>

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-white/5 rounded-lg p-2 text-center">
              <Clock className="w-4 h-4 mx-auto text-aura-text-muted mb-1" />
              <p className="text-xs text-aura-text-muted">Time</p>
              <p className="text-sm text-white font-medium">{selectedExercise.duration}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-2 text-center">
              <p className="text-xs text-aura-text-muted">Sets</p>
              <p className="text-sm text-white font-medium">{selectedExercise.sets}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-2 text-center">
              <p className="text-xs text-aura-text-muted">Reps</p>
              <p className="text-sm text-white font-medium">{selectedExercise.reps}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-2 text-center">
              <p className="text-xs text-aura-text-muted">Rest</p>
              <p className="text-sm text-white font-medium">{selectedExercise.rest}</p>
            </div>
          </div>

          {/* Instructions */}
          <div>
            <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">How to Perform</p>
            <ol className="space-y-2">
              {selectedExercise.instructions.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="w-5 h-5 rounded-full bg-aura-primary/20 text-aura-primary flex items-center justify-center flex-shrink-0 text-xs">
                    {i + 1}
                  </span>
                  <span className="text-white">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Coach Tip */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <p className="text-xs text-amber-400 uppercase tracking-wider mb-1">Coach Tip</p>
            <p className="text-sm text-white">{selectedExercise.coachTips}</p>
          </div>

          {/* Video Button */}
          {selectedExercise.videoUrl && (
            <a
              href={selectedExercise.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              data-testid="watch-video-btn"
            >
              <Youtube className="w-5 h-5" />
              Watch Video Tutorial
              <ExternalLink className="w-4 h-4" />
            </a>
          )}

          {/* Equipment & Sports Tags */}
          <div className="flex flex-wrap gap-2">
            {selectedExercise.equipment !== 'none' && (
              <Badge variant="outline" className="border-white/20 text-aura-text-muted">
                {selectedExercise.equipment === 'basic' ? 'Basic Equipment' : 'Full Gym'}
              </Badge>
            )}
            {selectedExercise.equipment === 'none' && (
              <Badge variant="outline" className="border-green-500/30 text-green-400">
                No Equipment
              </Badge>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExerciseLibraryProvider;
